import type { PlanCode } from "../types";
import type { Locale } from "../lib/i18n";
import { apiFetch } from "./client";

/** `GET /billing/plans` が返す有効なプラン。価格はこの API 応答を表示の正本にする。 */
export interface BillingPlan {
  code: PlanCode;
  name: string;
  /** JPY で請求される場合の月額。backend の BillingPlan.price_jpy_monthly と同じ整数。 */
  price_jpy_monthly: number;
  /** USD で請求される場合の月額。Decimal を精度の落ちない文字列で受け取る。 */
  price_usd_monthly: string;
  trial_days: number;
  feature_flags: Record<string, unknown>;
}

export interface SubscribeResponse {
  billing_operation_id: string | null;
  shop_id: number;
  plan_code: PlanCode;
  subscription_id?: number;
  shopify_charge_id?: string;
  /** 有料は Shopify の承認 URL、Free は課金が無いため null。 */
  confirmation_url: string | null;
  subscription_status: string;
}

export interface SyncBillingResponse {
  shop_id: number;
  active: boolean;
  status: string | null;
  plan_code: PlanCode | null;
  trial_ends_at?: string | null;
  billing_operation?: BillingOperation;
}

export interface BillingReturnContext {
  host: string;
  locale: Locale;
  return_path: string;
}

export interface BillingOperation extends BillingReturnContext {
  billing_operation_id: string;
  subscription_status: "pending_approval" | "active" | "frozen" | "cancelled" | "declined" | "expired";
  confirmation_url: string | null;
}

// 復帰文脈そのものはserverを正本とし、tabには再読に必要な操作IDだけを残す。
const PENDING_OPERATION_KEY = "dbx-billing-operation";

export function pendingBillingOperation(): string | null {
  try { return sessionStorage.getItem(PENDING_OPERATION_KEY); } catch { return null; }
}

export function clearPendingBillingOperation(expectedId?: string): void {
  const saved = pendingBillingOperation();
  if (!expectedId || saved === expectedId || saved === "current") {
    sessionStorage.removeItem(PENDING_OPERATION_KEY);
  }
}

export function getBillingOperation(id: string): Promise<BillingOperation> {
  return apiFetch<BillingOperation>(`/billing/operations/${encodeURIComponent(id)}`);
}

export function getCurrentBillingOperation(): Promise<BillingOperation> {
  return apiFetch<BillingOperation>("/billing/operations/current");
}

/** 公開中の有効なプランと、その時点の請求価格を取得する。 */
export function listPlans(): Promise<BillingPlan[]> {
  return apiFetch<BillingPlan[]>("/billing/plans");
}

/**
 * ユーザーが明示的に選んだプランの購読を開始する。
 * 店舗は planCode ではなく session token から backend が決定する。
 */
export async function subscribe(planCode: PlanCode, context: BillingReturnContext): Promise<SubscribeResponse> {
  // 応答前のunmount/reloadやunknownも、次回起動で保存状態の回復へ誘導する。
  sessionStorage.setItem(PENDING_OPERATION_KEY, "current");
  const response = await apiFetch<SubscribeResponse>("/billing/subscribe", {
    method: "POST",
    query: { plan_code: planCode },
    body: context,
  });
  if (response.billing_operation_id) {
    sessionStorage.setItem(PENDING_OPERATION_KEY, response.billing_operation_id);
  } else if (!response.confirmation_url) {
    clearPendingBillingOperation();
  }
  return response;
}

/** Shopify 側の activeSubscriptions を正としてローカルの課金状態を収束させる。 */
export function syncBilling(operationId?: string): Promise<SyncBillingResponse> {
  return apiFetch<SyncBillingResponse>("/billing/sync", {
    method: "POST",
    ...(operationId ? { query: { billing_operation_id: operationId } } : {}),
  });
}
